import { Link } from "react-router";
import {
  Monitor,
  Server,
  MessageSquare,
  Lock,
  GitBranch,
  Users,
  Shield,
  AlertTriangle,
  DollarSign,
  FileWarning,
  Building2,
  Rocket,
  ChevronRight,
  CheckCircle2,
  ArrowRight,
  Search,
  Brain,
  Activity,
  Tag,
  ShieldAlert,
  BookOpen,
  Route,
  Video,
  Workflow,
  GraduationCap,
  PackageCheck,
  BarChart3,
  ScanEye,
  Image,
  Bell,
  Flag,
  Database,
  Cloud,
  Mic,
  Volume2,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { SESSION_AUDIO_MODES, SESSION_AUDIO_PLAN_MATRIX } from "../data/audioCaptureContent";

const painPoints = [
  {
    icon: FileWarning,
    title: "Incidents without evidence",
    description:
      "When regulators, auditors, or lawyers ask what happened on a specific date, you have logs that show actions, but not what the operator actually saw or intended.",
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
  },
  {
    icon: DollarSign,
    title: "Disputes that drain capital",
    description:
      "Customer complaints, vendor disputes, and internal investigations become expensive he-said-she-said battles where the truth is impossible to prove.",
    color: "text-rose-400",
    bg: "bg-rose-500/10",
    border: "border-rose-500/20",
  },
  {
    icon: AlertTriangle,
    title: "Compliance gaps that compound",
    description:
      "SOX, GxP, HIPAA, and industry audits demand proof that a person actually performed each step. Chat logs and system timestamps are necessary but rarely sufficient.",
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
  },
];

const costOfInaction = [
  {
    stat: "$4.88M",
    label: "Average cost of a data breach in 2024",
    source: "IBM Security",
  },
  {
    stat: "287 days",
    label: "Average time to identify and contain a breach",
    source: "IBM Security",
  },
  {
    stat: "73%",
    label: "Of insider threat incidents lack sufficient evidence for action",
    source: "Ponemon Institute",
  },
];

const companyBenefits = [
  {
    icon: Rocket,
    title: "For Growing Companies",
    subtitle: "Growing companies",
    benefits: [
      "Build audit-readiness before your first SOC 2 or enterprise customer audit",
      "Create evidence trails that support investor security questionnaires",
      "Protect against employee disputes as you scale remote teams globally",
      "Avoid compliance work that piles up if you wait",
    ],
  },
  {
    icon: Building2,
    title: "For Enterprise Organizations",
    subtitle: "Regulated industries and global operations",
    benefits: [
      "Respond to examiner requests with visual proof instead of reconstructed narratives",
      "Reduce legal discovery costs with searchable, timestamped evidence",
      "Extend oversight to outsourced and offshore teams without always-on recording",
      "Accelerate incident response with forensic-grade visual context",
    ],
  },
];

const capabilities = [
  {
    icon: Monitor,
    title: "Capture only what matters",
    benefit: "Record specific applications and workflows, not endless idle time",
    description:
      "Policy-driven triggers activate recording when operators touch critical systems. No ambient surveillance, no noise, no privacy overreach.",
  },
  {
    icon: Volume2,
    title: "Optional session audio",
    benefit: "Speaker output on Pro; speaker plus microphone on Enterprise",
    description:
      "Add synchronized audio to session recordings when your organization enables it: what plays through the speakers (meetings, softphone, media) on Pro; microphone plus speaker output on Enterprise.",
  },
  {
    icon: Server,
    title: "Keep evidence in your control",
    benefit: "Self-hosted on your own servers, never on third-party clouds",
    description:
      "Session streams, archives, and indexes stay inside your security perimeter. You control regions, encryption, and access boundaries.",
  },
  {
    icon: Database,
    title: "Flexible storage and Google Drive archive",
    benefit: "Pro and Enterprise: archive to Workspace Drive before short-term storage ends",
    description:
      "Enterprise adds use-your-own-storage (Cloudflare R2 or AWS S3, one per organization). On Pro and Enterprise, investigators can copy recordings to your Google Drive or Shared drive so evidence outlives the default retention window while screen-text search (OCR) and search stay in the console.",
  },
  {
    icon: MessageSquare,
    title: "Ask questions, get timestamped answers",
    benefit: "Find the exact moment in seconds, not hours of scrubbing video",
    description:
      'Natural language search over session content: "Show me when the wire instruction was confirmed" returns a precise timestamp with visual context.',
  },
  {
    icon: GitBranch,
    title: "Evidence that survives scrutiny",
    benefit: "Unchangeable evidence record for legal, HR, and regulatory proceedings",
    description:
      "Tamper-evident metadata, access logs, and export packaging designed for disputes, investigations, and examiner requests.",
  },
  {
    icon: Users,
    title: "Access aligned to your org chart",
    benefit: "Separate who configures, queries, and exports, mapped to your policies",
    description:
      "RBAC (Role-Based Access Control) ensures security, HR, legal, and operations each see only what they are authorized to review.",
  },
  {
    icon: Lock,
    title: "Security that earns trust",
    benefit: "Software with only the minimum access it needs, plus modern encryption",
    description:
      "Agents operate with minimal OS permissions. Encrypted connections throughout, so evidence stays within the policies you define.",
  },
];

const proofPoints = [
  { value: "99.97%", label: "Forensic accuracy" },
  { value: "<2ms", label: "Stream latency" },
  { value: "0 bytes", label: "Third-party egress (Enterprise self-hosted)" },
  { value: "100%", label: "Self-hosted" },
];

const enterpriseAiCapabilities = [
  {
    icon: PackageCheck,
    title: "Forensic Evidence Export",
    tagline: "One-click court-ready evidence packages with chain of custody",
  },
  {
    icon: BarChart3,
    title: "Activity Intelligence Reports",
    tagline: "Automated daily summaries of endpoint activity",
  },
  {
    icon: Activity,
    title: "Behavioral Anomaly Alerts",
    tagline: "AI spots unusual patterns before they become incidents",
  },
  {
    icon: Tag,
    title: "Smart Content Classification",
    tagline: "Automatic PII, risk, and compliance tagging",
  },
  {
    icon: ScanEye,
    title: "Sensitive Data Detection",
    tagline: "Auto-detect personal and financial data on screen",
  },
  {
    icon: ShieldAlert,
    title: "Workplace Conduct Monitor",
    tagline: "AI flags potential policy violations and harassment",
  },
  {
    icon: Shield,
    title: "Content Safety Screening",
    tagline: "Detect inappropriate or explicit material",
  },
  {
    icon: BookOpen,
    title: "Golden Path Learning",
    tagline: "AI learns your SOPs from actual behavior",
  },
  {
    icon: Route,
    title: "Data Journey Tracking",
    tagline: "Follow sensitive data across every endpoint that touched it",
  },
  {
    icon: Brain,
    title: "Compliance Auto-Tagging",
    tagline: "Recordings automatically tagged by regulation (SOX, HIPAA, GDPR)",
  },
  {
    icon: Video,
    title: "Live AI Surveillance",
    tagline: "Real-time AI monitoring with instant alerts",
  },
  {
    icon: Workflow,
    title: "Visual Process Discovery",
    tagline: "Auto-generate workflow diagrams from actual recordings",
  },
  {
    icon: GraduationCap,
    title: "Shadow Training Mode",
    tagline: "Train new hires with anonymized recordings of top performers",
  },
];

const advancedOcrCapabilities = [
  {
    icon: Search,
    title: "Visual Concept Search",
    tagline:
      'Type any concept and find visually matching segments: "mobile phone", "gun", "person at desk", "drug paraphernalia"',
  },
  {
    icon: Image,
    title: "Similar Image Search",
    tagline:
      "Upload 1-3 reference screenshots or photos and find visually similar footage across your entire archive",
  },
  {
    icon: Server,
    title: "On-Device Visual AI",
    tagline:
      "Visual AI runs entirely on your server: no images or queries sent to any external service",
  },
  {
    icon: MessageSquare,
    title: "No Text Required",
    tagline:
      "Find what you are looking for even when nothing relevant appeared as on-screen text, searching by visual content alone",
  },
];

const enterpriseVisualPolicyCapabilities = [
  {
    icon: ScanEye,
    title: "Automated NSFW Detection",
    tagline:
      "Purpose-built AI model with 96.5% accuracy: every recorded segment scanned automatically, one occurrence triggers an instant alert",
  },
  {
    icon: Shield,
    title: "Visual Policy Rule Engine",
    tagline:
      "Define unlimited custom rules: type any concept or upload a reference image as the trigger condition",
  },
  {
    icon: Flag,
    title: "Political and Religious Content Monitoring",
    tagline:
      "Detect campaign material, party imagery, and extremist religious content on work devices",
  },
  {
    icon: AlertTriangle,
    title: "Weapon, Substance and Harassment Detection",
    tagline:
      "Catches weapons, alcohol, controlled substances, gambling imagery, competitor material, and harassment content",
  },
  {
    icon: Bell,
    title: "Policy Alerts Dashboard",
    tagline:
      "Dedicated review workflow for security and HR teams: triage flagged segments, mark as reviewed, filter by rule, severity, or endpoint",
  },
  {
    icon: Activity,
    title: "Single-Occurrence Alerting",
    tagline:
      "One occurrence is enough: no waiting for repeated violations before an alert fires",
  },
  {
    icon: BookOpen,
    title: "Full Audit Trail",
    tagline:
      "Every alert and review action logged for compliance, immutable record from detection to resolution",
  },
];

const SOC2_TSC_DISCLAIMER =
  "DeskReplay maps to Trust Services Criteria. We do not claim SOC 2 Type II certification.";

const trustBadges: { label: string; soc2Footnote?: boolean }[] = [
  { label: "GDPR: right to delete" },
  { label: "HIPAA-oriented controls" },
  { label: "SOC 2 TSC alignment", soc2Footnote: true },
  { label: "ISO 27001 control mapping" },
  { label: "DPDP-oriented controls" },
  { label: "FINRA / MiFID II audit readiness" },
  { label: "Okta & Entra ID SSO" },
  { label: "SIEM-compatible" },
  { label: "Forensic chain of custody" },
  { label: "SHA-256 tamper-evident" },
  { label: "Enterprise: your own servers" },
  { label: "Zero-trust ready" },
];

export function Features() {
  return (
    <>
      <Seo
        title="Features"
        description="Explore DeskReplay features: policy-triggered capture, optional session audio (system on Pro, system plus mic on Enterprise), self-hosted deployment, AI-powered visual search, chain-of-custody exports, and role-based access control."
        canonicalPath="/features"
      />
      <div className="bg-slate-950 min-h-screen">
      {/* ===== HERO: OUTCOME-FOCUSED ===== */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(13, 148, 136, 0.14) 0%, transparent 55%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1
              className="text-slate-100 mb-6"
              style={{
                fontSize: "clamp(2.2rem, 4.5vw, 3.5rem)",
                fontWeight: 800,
                letterSpacing: "-0.03em",
                lineHeight: "1.1",
              }}
            >
              Resolve Disputes, Support Audit Responses, and Strengthen Compliance Evidence{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, var(--color-teal-500, #14b8a6) 0%, var(--color-emerald-500, #10b981) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Without Ambiguous Logs
              </span>
            </h1>
            <p className="text-on-dark-muted text-xl leading-relaxed mb-10">
              When a single click can cost millions, memory is not evidence. DeskReplay captures forensic-grade visual
              truth for high-stakes workflows, so you can prove exactly what happened, when, and who did it.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/request-demo"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-8 py-4 rounded-xl text-base font-semibold transition-all hover:shadow-lg hover:shadow-teal-500/25"
              >
                Schedule Demo
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center gap-2 border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 px-8 py-4 rounded-xl text-base font-medium transition-colors"
              >
                See Use Cases
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== COMPLIANCE TRUST STRIP ===== */}
      <section className="py-10 border-y border-slate-800/60 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 text-xs uppercase tracking-widest mb-5" style={{ fontWeight: 600 }}>
            Trusted compliance frameworks &amp; integrations
          </p>
          <div className="flex flex-wrap justify-center items-center gap-2.5">
            {trustBadges.map((badge) => (
              <span
                key={badge.label}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-700/60 bg-slate-800/50 text-slate-300 text-xs hover:border-teal-500/40 hover:text-slate-100 transition-colors"
                style={{ fontWeight: 500 }}
                title={badge.soc2Footnote ? SOC2_TSC_DISCLAIMER : undefined}
              >
                <Shield size={10} className="text-teal-400 shrink-0" />
                {badge.label}
                {badge.soc2Footnote ? (
                  <span
                    className="text-teal-400/90 font-semibold leading-none cursor-help"
                    title={SOC2_TSC_DISCLAIMER}
                    aria-hidden
                  >
                    *
                  </span>
                ) : null}
              </span>
            ))}
          </div>
          <p
            id="features-soc2-tsc-footnote"
            className="text-center text-slate-500 text-[11px] mt-4 max-w-xl mx-auto leading-relaxed px-2"
          >
            <span className="text-teal-500/90 font-semibold" aria-hidden>
              *
            </span>{" "}
            {SOC2_TSC_DISCLAIMER}
          </p>
        </div>
      </section>

      {/* ===== PROBLEM: NAME THE PAIN ===== */}
      <section className="py-24 relative">
        <div
          className="absolute inset-0 opacity-[0.008]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(148, 163, 184, 1) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 1) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p
              className="text-amber-400 text-sm uppercase tracking-widest mb-4"
              style={{ fontWeight: 600 }}
            >
              The Problem
            </p>
            <h2
              className="text-slate-100 max-w-3xl mx-auto"
              style={{
                fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: "1.15",
              }}
            >
              You have logs. You have timestamps.{" "}
              <span className="text-on-dark-muted">But do you have proof?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {painPoints.map((pain) => (
              <div
                key={pain.title}
                className={`relative rounded-2xl border p-8 ${pain.border}`}
                style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
              >
                <div
                  className={`w-12 h-12 rounded-xl ${pain.bg} flex items-center justify-center mb-6`}
                >
                  <pain.icon size={22} className={pain.color} />
                </div>
                <h3
                  className="text-slate-100 mb-3"
                  style={{ fontSize: "1.25rem", fontWeight: 700 }}
                >
                  {pain.title}
                </h3>
                <p className="text-on-dark-muted text-sm leading-relaxed">
                  {pain.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== AGITATE: COST OF INACTION ===== */}
      <section
        className="py-20 border-y border-slate-800/50"
        style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p
              className="text-rose-400 text-sm uppercase tracking-widest mb-4"
              style={{ fontWeight: 600 }}
            >
              The Cost of Inaction
            </p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{
                fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: "1.2",
              }}
            >
              Every incident without evidence is a liability waiting to compound.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {costOfInaction.map((item) => (
              <div key={item.label} className="text-center">
                <div
                  className="text-rose-400 mb-2"
                  style={{ fontSize: "2.5rem", fontWeight: 800, letterSpacing: "-0.03em" }}
                >
                  {item.stat}
                </div>
                <p className="text-slate-300 text-sm mb-1">{item.label}</p>
                <p className="text-on-dark-muted text-xs">{item.source}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SOLUTION: INTRODUCE DESKREPLAY ===== */}
      <section className="py-24 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 50% 30%, rgba(13, 148, 136, 0.4) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p
              className="text-teal-400 text-sm uppercase tracking-widest mb-4"
              style={{ fontWeight: 600 }}
            >
              The Solution
            </p>
            <h2
              className="text-slate-100 max-w-3xl mx-auto mb-6"
              style={{
                fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: "1.15",
              }}
            >
              DeskReplay: The Visual{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, var(--color-teal-500, #14b8a6) 0%, var(--color-emerald-500, #10b981) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                System of Record
              </span>{" "}
              for Mission-Critical Work
            </h2>
            <p className="text-on-dark-muted text-lg max-w-2xl mx-auto">
              Policy-driven capture, self-hosted in your infrastructure, with AI-powered search that
              turns raw video into actionable evidence. No ambient surveillance. No data leaving your control.
            </p>
          </div>

          {/* Proof points */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
            {proofPoints.map((point) => (
              <div
                key={point.label}
                className="text-center p-6 rounded-xl border border-teal-500/20 bg-teal-500/5"
              >
                <div
                  className="text-teal-400 mb-1"
                  style={{ fontSize: "1.75rem", fontWeight: 800, letterSpacing: "-0.03em" }}
                >
                  {point.value}
                </div>
                <div className="text-on-dark-muted text-sm">{point.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STORAGE & ARCHIVE ===== */}
      <section className="py-20 bg-slate-950 border-b border-slate-800/60" id="storage-archive">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-4">
              Recording storage
            </p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
            >
              Short-term storage, your own bucket, or Google Drive archive
            </h2>
            <p className="text-on-dark-muted text-base max-w-2xl mx-auto mt-4 leading-relaxed">
              DeskReplay holds the searchable index. You choose where long-lived video evidence lives.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-2xl border border-slate-700/60 bg-slate-900/50 p-6">
              <Cloud className="text-teal-400 mb-4" size={28} />
              <p className="text-xs font-semibold uppercase tracking-wider text-teal-300 mb-2">Pro and Enterprise</p>
              <h3 className="text-slate-100 font-semibold mb-2">Google Drive archive</h3>
              <p className="text-on-dark-muted text-sm leading-relaxed">
                Register your org&apos;s Google login in the admin console, connect Workspace, then archive any
                recording to your Drive or Shared drive before short-term storage removes it. Archived clips stay
                searchable in DeskReplay with a link to Drive.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-700/60 bg-slate-900/50 p-6">
              <Database className="text-emerald-400 mb-4" size={28} />
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300 mb-2">Enterprise</p>
              <h3 className="text-slate-100 font-semibold mb-2">Cloudflare R2 or AWS S3 (your own bucket)</h3>
              <p className="text-on-dark-muted text-sm leading-relaxed">
                Point new recordings at your own object storage bucket. Pick one primary provider per
                organization (R2 or AWS S3, not both). Recordings in progress keep their original storage until they end.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-700/60 bg-slate-900/50 p-6">
              <Server className="text-teal-400 mb-4" size={28} />
              <p className="text-xs font-semibold uppercase tracking-wider text-teal-300 mb-2">Enterprise self-hosted</p>
              <h3 className="text-slate-100 font-semibold mb-2">Your own servers, your own disks</h3>
              <p className="text-on-dark-muted text-sm leading-relaxed">
                Self-hosted deployments can use local disk or storage you operate inside your network, with the same
                optional Google Drive archive workflow for recordings you need to keep longer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SESSION AUDIO ===== */}
      <section className="py-20 bg-slate-950 border-b border-slate-800/60" id="session-audio">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-emerald-400 text-xs uppercase tracking-widest font-semibold mb-4">Session audio</p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
            >
              System audio and mic audio streaming
            </h2>
            <p className="text-on-dark-muted text-base max-w-2xl mx-auto mt-4 leading-relaxed">
              Session Audio is an optional add-on purchased per seat, organization-gated, and bound to capture policies.
              Base Essential seats are screen-only without the module.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="rounded-2xl border border-slate-700/60 bg-slate-900/50 p-6">
              <Volume2 className="text-emerald-400 mb-4" size={28} />
              <h3 className="text-slate-100 font-semibold mb-2">{SESSION_AUDIO_MODES.system.title}</h3>
              <p className="text-on-dark-muted text-sm leading-relaxed mb-3">{SESSION_AUDIO_MODES.system.description}</p>
              <p className="text-emerald-300 text-xs font-semibold uppercase tracking-wider">Session Audio Pro add-on</p>
            </div>
            <div className="rounded-2xl border border-slate-700/60 bg-slate-900/50 p-6">
              <Mic className="text-teal-400 mb-4" size={28} />
              <h3 className="text-slate-100 font-semibold mb-2">{SESSION_AUDIO_MODES.mic.title}</h3>
              <p className="text-on-dark-muted text-sm leading-relaxed mb-3">{SESSION_AUDIO_MODES.mic.description}</p>
              <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider">Session Audio Enterprise add-on</p>
            </div>
            <div className="rounded-2xl border border-slate-700/60 bg-slate-900/50 p-6">
              <Monitor className="text-slate-400 mb-4" size={28} />
              <h3 className="text-slate-100 font-semibold mb-2">Essential</h3>
              <p className="text-on-dark-muted text-sm leading-relaxed mb-3">
                {SESSION_AUDIO_PLAN_MATRIX.essential.detail}
              </p>
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Screen only</p>
            </div>
          </div>
          <p className="text-center text-on-dark-muted text-sm max-w-2xl mx-auto leading-relaxed">
            Enable session audio in organization Settings, create audio profiles, and attach them to proportionate
            capture policies. Work with HR and counsel on notice before enabling microphone capture.
          </p>
          <p className="text-center mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/pricing#session-audio-addon"
              className="text-teal-400 text-sm font-medium hover:text-teal-300 inline-flex items-center gap-1"
            >
              Session Audio add-on pricing
              <ChevronRight size={14} />
            </Link>
            <Link
              to="/resources/guide-session-audio-capture"
              className="text-teal-400 text-sm font-medium hover:text-teal-300 inline-flex items-center gap-1"
            >
              Read the session audio guide
              <ChevronRight size={14} />
            </Link>
            <Link
              to="/resources/guide-session-audio-forensic-replay"
              className="text-teal-400 text-sm font-medium hover:text-teal-300 inline-flex items-center gap-1"
            >
              Forensic replay and ZIP export
              <ChevronRight size={14} />
            </Link>
          </p>
        </div>
      </section>

      {/* ===== WEBCAM CAPTURE ===== */}
      <section className="py-20 bg-slate-950 border-b border-slate-800/60" id="webcam-capture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-violet-400 text-xs uppercase tracking-widest font-semibold mb-4">Webcam Capture add-on</p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
            >
              Identity verification at the keyboard
            </h2>
            <p className="text-on-dark-muted text-base max-w-2xl mx-auto mt-4 leading-relaxed">
              Timed JPEG snapshots tied to screen recording policies so reviewers can confirm who was at the workstation,
              not just what was on screen. Optional module purchased per seat on Pro+ endpoint pools.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="rounded-2xl border border-slate-700/60 bg-slate-900/50 p-6">
              <h3 className="text-slate-100 font-semibold mb-2">Webcam Capture Pro</h3>
              <p className="text-on-dark-muted text-sm leading-relaxed">
                Fixed and random interval snapshot schedules with Standard quality (Low/Medium profile settings).
              </p>
            </div>
            <div className="rounded-2xl border border-violet-500/30 bg-slate-900/50 p-6">
              <h3 className="text-slate-100 font-semibold mb-2">Webcam Capture Enterprise</h3>
              <p className="text-on-dark-muted text-sm leading-relaxed">
                Adds app trigger and system startup capture plus Best quality settings (hardware-dependent).
              </p>
            </div>
          </div>
          <div className="rounded-xl border border-violet-500/25 bg-violet-500/5 p-5 max-w-3xl mx-auto">
            <p className="text-violet-100/90 text-sm leading-relaxed">
              Each snapshot briefly activates the device camera and the indicator light will turn on. DeskReplay does not
              disable hardware indicators. Typical indicator duration is under about one second per capture, depending on
              OS and driver.
            </p>
          </div>
          <p className="text-center mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link
              to="/resources/guide-webcam-capture"
              className="text-teal-400 text-sm font-medium hover:text-teal-300 inline-flex items-center gap-1"
            >
              Webcam Capture guide
              <ChevronRight size={14} />
            </Link>
            <Link
              to="/pricing#webcam-capture-addon"
              className="text-teal-400 text-sm font-medium hover:text-teal-300 inline-flex items-center gap-1"
            >
              Webcam Capture add-on pricing
              <ChevronRight size={14} />
            </Link>
          </p>
        </div>
      </section>

      {/* ===== WHO IS THIS FOR: COMPANY SIZE BENEFITS ===== */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p
              className="text-emerald-400 text-sm uppercase tracking-widest mb-4"
              style={{ fontWeight: 600 }}
            >
              Built for Your Stage
            </p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{
                fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: "1.15",
              }}
            >
              Whether you are scaling fast or operating at scale
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {companyBenefits.map((segment) => (
              <div
                key={segment.title}
                className="rounded-2xl border border-teal-500/15 p-8 hover:border-teal-500/30 transition-all"
                style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-teal-500/15 flex items-center justify-center">
                    <segment.icon size={28} className="text-teal-400" />
                  </div>
                  <div>
                    <h3
                      className="text-slate-100"
                      style={{ fontSize: "1.3rem", fontWeight: 700 }}
                    >
                      {segment.title}
                    </h3>
                    <p className="text-teal-400 text-sm">{segment.subtitle}</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {segment.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-3">
                      <CheckCircle2
                        size={18}
                        className="text-emerald-400 shrink-0 mt-0.5"
                      />
                      <span className="text-on-dark-muted text-sm leading-relaxed">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CAPABILITIES: FEATURES AS BENEFITS ===== */}
      <section
        className="py-24 relative"
        style={{ background: "linear-gradient(180deg, var(--color-slate-950) 0%, var(--color-slate-900) 50%, var(--color-slate-950) 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p
              className="text-teal-400 text-sm uppercase tracking-widest mb-4"
              style={{ fontWeight: 600 }}
            >
              How It Works
            </p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{
                fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: "1.15",
              }}
            >
              Everything you need to create{" "}
              <span className="text-on-dark-muted">defensible evidence</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap) => (
              <div
                key={cap.title}
                className="rounded-2xl border border-teal-500/15 p-8 hover:border-teal-500/25 transition-all"
                style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
              >
                <div className="w-12 h-12 rounded-xl bg-teal-500/15 flex items-center justify-center mb-5">
                  <cap.icon size={24} className="text-teal-400" />
                </div>
                <h3
                  className="text-slate-100 mb-2"
                  style={{ fontSize: "1.15rem", fontWeight: 700 }}
                >
                  {cap.title}
                </h3>
                <p
                  className="text-teal-400 text-sm mb-3"
                  style={{ fontWeight: 500 }}
                >
                  {cap.benefit}
                </p>
                <p className="text-on-dark-muted text-sm leading-relaxed">
                  {cap.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TRAINING INTELLIGENCE ===== */}
      <section className="py-20 bg-slate-950 border-b border-slate-800/60" id="training-intelligence">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-3">Training Intelligence</p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto mb-4"
              style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.2" }}
            >
              The fastest way to coach is to{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, var(--color-teal-500, #14b8a6) 0%, var(--color-emerald-500, #10b981) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                show, not tell.
              </span>
            </h2>
            <p className="text-on-dark-muted text-base max-w-2xl mx-auto leading-relaxed">
              Every session you record is a potential training asset. Use the same archive that powers compliance and
              audit to build a coaching library of your top performers. No extra software, no separate tooling.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div
              className="rounded-2xl border border-teal-500/20 p-7 flex flex-col"
              style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--color-teal-500) 9%, var(--color-slate-900)) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="w-11 h-11 rounded-xl bg-teal-500/15 flex items-center justify-center mb-5">
                <Search size={20} className="text-teal-400" />
              </div>
              <h3 className="text-slate-100 font-bold text-base mb-2">Find the Teaching Moment</h3>
              <p className="text-on-dark-muted text-sm leading-relaxed flex-1">
                Use AI semantic search to surface sessions where your top performers followed the SOP perfectly, handled
                a difficult customer interaction, or demonstrated the correct process. Locate the exact timestamp in seconds.
              </p>
              <p className="text-teal-400 text-xs font-semibold mt-4 uppercase tracking-wide">Pro and Enterprise</p>
            </div>

            <div
              className="rounded-2xl border border-emerald-500/20 p-7 flex flex-col"
              style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--color-emerald-500) 9%, var(--color-slate-900)) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center mb-5">
                <Video size={20} className="text-emerald-400" />
              </div>
              <h3 className="text-slate-100 font-bold text-base mb-2">Playback with Precision</h3>
              <p className="text-on-dark-muted text-sm leading-relaxed flex-1">
                Session player with variable speed control, on-screen text panel, and timestamped navigation. Managers can
                jump directly to the key moment, slow it down, and annotate what the team should observe.
              </p>
              <p className="text-teal-400 text-xs font-semibold mt-4 uppercase tracking-wide">All plans</p>
            </div>

            <div
              className="rounded-2xl border border-teal-500/20 p-7 flex flex-col"
              style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="w-11 h-11 rounded-xl bg-teal-500/15 flex items-center justify-center mb-5">
                <GraduationCap size={20} className="text-teal-400" />
              </div>
              <h3 className="text-slate-100 font-bold text-base mb-2">Consistent Onboarding at Scale</h3>
              <p className="text-on-dark-muted text-sm leading-relaxed flex-1">
                Every new hire, remote team member, or offshore contractor learns from the same gold-standard sessions.
                Not a different manager's memory each time. Not a slide deck approximation. The actual work, on screen.
              </p>
              <p className="text-teal-400 text-xs font-semibold mt-4 uppercase tracking-wide">All plans</p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 px-6 py-4">
            <p className="text-on-dark-muted text-sm leading-relaxed">
              <span className="text-slate-200 font-semibold">Ghost coaching in practice:</span>{" "}
              Your compliance recordings and your coaching library are the same archive. Capturing for audit automatically creates a training resource, at no extra cost or effort.
            </p>
          </div>
        </div>
      </section>

      {/* ===== AI INTELLIGENCE ===== */}
      <section
        className="py-24 relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, var(--color-slate-950) 0%, var(--color-slate-900) 50%, var(--color-slate-950) 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 50% 30%, rgba(13, 148, 136, 0.5) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p
              className="text-teal-400 text-sm uppercase tracking-widest mb-4"
              style={{ fontWeight: 600 }}
            >
              AI Intelligence
            </p>
            <h2
              className="text-slate-100 max-w-3xl mx-auto mb-6"
              style={{
                fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: "1.15",
              }}
            >
              Search your recordings like you{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, var(--color-teal-500, #14b8a6) 0%, var(--color-emerald-500, #10b981) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                search the web
              </span>
            </h2>
            <p className="text-on-dark-muted text-lg max-w-2xl mx-auto">
              AI processing is configurable: bring your own keys for OpenAI, Google Gemini, Claude, DeepSeek, Grok,
              or Azure OpenAI, or run Ollama on your own hardware. Enterprise customers can keep all inference
              on-premises with zero data egress.
            </p>
          </div>

          {/* Pro features: Chat + Visual Search */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Chat with Your Recordings */}
            <div
              className="rounded-2xl border border-teal-500/25 p-8 relative"
              style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-teal-500/15 flex items-center justify-center">
                  <MessageSquare size={24} className="text-teal-400" />
                </div>
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold border border-teal-500/40 bg-teal-500/10 text-teal-300">
                  Pro
                </span>
              </div>
              <h3 className="text-slate-100 mb-2" style={{ fontSize: "1.3rem", fontWeight: 700 }}>
                Chat with Your Recordings
              </h3>
              <p className="text-teal-400 text-sm mb-3" style={{ fontWeight: 500 }}>
                Answers in seconds, not hours of scrubbing video
              </p>
              <p className="text-on-dark-muted text-sm leading-relaxed">
                Talk to your video archive. Ask questions in plain English, get timestamped answers
                in seconds. Ask "When did the agent open the refund screen?" and jump straight to
                that moment.
              </p>
            </div>

            {/* Basic OCR */}
            <div
              className="rounded-2xl border border-teal-500/25 p-8 relative"
              style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-teal-500/15 flex items-center justify-center">
                  <Search size={24} className="text-teal-400" />
                </div>
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold border border-teal-500/40 bg-teal-500/10 text-teal-300">
                  Pro
                </span>
              </div>
              <h3 className="text-slate-100 mb-2" style={{ fontSize: "1.3rem", fontWeight: 700 }}>
                Basic OCR
              </h3>
              <p className="text-teal-400 text-sm mb-3" style={{ fontWeight: 500 }}>
                Find any text that appeared on screen, instantly
              </p>
              <p className="text-on-dark-muted text-sm leading-relaxed">
                Basic screen-text search (OCR) indexes every recording when saved so you can search for any text that appeared
                on screen: application names, error messages, customer names, document contents. Find the
                exact recording and timestamp across your entire archive.
              </p>
            </div>
          </div>

          {/* Advanced OCR - Enterprise */}
          <div
            className="rounded-2xl border border-teal-500/25 p-8 mb-6"
            style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-teal-500/15 flex items-center justify-center">
                <Image size={20} className="text-teal-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-slate-100 font-bold text-lg">Advanced OCR</h3>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold border border-teal-400/40 bg-teal-400/10 text-teal-200">
                    Enterprise
                  </span>
                </div>
                <p className="text-on-dark-muted text-sm mt-0.5">
                  Visual concept and image search: no on-screen text required.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {advancedOcrCapabilities.map((cap) => (
                <div
                  key={cap.title}
                  className="flex items-start gap-3 rounded-xl border border-teal-500/10 p-4 hover:border-teal-500/25 transition-all"
                  style={{ background: "rgba(13, 148, 136, 0.04)" }}
                >
                  <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <cap.icon size={16} className="text-teal-400" />
                  </div>
                  <div>
                    <p className="text-slate-200 text-sm font-semibold leading-snug">{cap.title}</p>
                    <p className="text-on-dark-muted text-xs leading-relaxed mt-1">{cap.tagline}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Enterprise AI grid */}
          <div className="rounded-2xl border border-teal-600/20 p-8" style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-teal-500/15 flex items-center justify-center">
                <Brain size={20} className="text-teal-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-slate-100 font-bold text-lg">Enterprise AI Capabilities</h3>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold border border-teal-400/40 bg-teal-400/10 text-teal-200">
                    Enterprise
                  </span>
                </div>
                <p className="text-on-dark-muted text-sm mt-0.5">
                  The full AI Intelligence suite: your data, your infrastructure, zero egress. Pricing and{" "}
                  <Link to="/pricing/compare" className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
                    compare plans
                  </Link>{" "}
                  use grouped capability names; this page shows individual modules.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {enterpriseAiCapabilities.map((cap) => (
                <div
                  key={cap.title}
                  className="flex items-start gap-3 rounded-xl border border-teal-500/10 p-4 hover:border-teal-500/25 transition-all"
                  style={{ background: "rgba(13, 148, 136, 0.04)" }}
                >
                  <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <cap.icon size={16} className="text-teal-400" />
                  </div>
                  <div>
                    <p className="text-slate-200 text-sm font-semibold leading-snug">{cap.title}</p>
                    <p className="text-on-dark-muted text-xs leading-relaxed mt-1">{cap.tagline}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-teal-500/10 flex flex-wrap items-center justify-between gap-4">
              <p className="text-on-dark-muted text-sm max-w-xl">
                Text-based AI can run on your own infrastructure or via BYOK to OpenAI, Gemini, Claude, DeepSeek,
                Grok, or Azure OpenAI. Visual search and policy detection always run locally on your DeskReplay server.
              </p>
              <Link
                to="/request-demo?plan=enterprise"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-teal-500/25"
              >
                Request Enterprise Briefing
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>

          {/* Visual Policy Enforcement - Enterprise */}
          <div
            className="rounded-2xl border border-teal-600/20 p-8 mt-6"
            style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-teal-500/15 flex items-center justify-center">
                <ShieldAlert size={20} className="text-teal-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-slate-100 font-bold text-lg">Visual Policy Enforcement</h3>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold border border-teal-400/40 bg-teal-400/10 text-teal-200">
                    Enterprise
                  </span>
                </div>
                <p className="text-on-dark-muted text-sm mt-0.5">
                  Automated content scanning and policy alerts: no manual review queue required.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {enterpriseVisualPolicyCapabilities.map((cap) => (
                <div
                  key={cap.title}
                  className="flex items-start gap-3 rounded-xl border border-teal-500/10 p-4 hover:border-teal-500/25 transition-all"
                  style={{ background: "rgba(13, 148, 136, 0.04)" }}
                >
                  <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <cap.icon size={16} className="text-teal-400" />
                  </div>
                  <div>
                    <p className="text-slate-200 text-sm font-semibold leading-snug">{cap.title}</p>
                    <p className="text-on-dark-muted text-xs leading-relaxed mt-1">{cap.tagline}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-teal-500/10 flex flex-wrap items-center justify-between gap-4">
              <p className="text-on-dark-muted text-sm max-w-xl">
                All visual AI runs on-device on your self-hosted server. No segment images, frames,
                or employee data are ever sent to any external service or cloud API.
              </p>
              <Link
                to="/request-demo?plan=enterprise"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-teal-500/25"
              >
                Request Enterprise Briefing
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== OPTIONAL CCTV+ ADD-ON (not included in base desktop recording) ===== */}
      <section
        id="cctv-intelligence"
        className="py-24 relative overflow-hidden border-t border-amber-500/10"
        style={{ background: "linear-gradient(180deg, var(--color-slate-950) 0%, var(--color-slate-900) 50%, var(--color-slate-950) 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 50% 30%, rgba(217, 119, 6, 0.4) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 mb-5">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              <span className="text-amber-200 text-xs font-semibold uppercase tracking-wider">Optional add-on · purchased separately</span>
            </div>
            <h2
              className="text-slate-100 max-w-3xl mx-auto mb-6"
              style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.15" }}
            >
              CCTV+ Plugin:{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, var(--color-amber-400, #fbbf24) 0%, var(--color-amber-300, #fcd34d) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Hot Path. Cold Path.
              </span>
            </h2>
            <p className="text-on-dark-muted text-lg max-w-2xl mx-auto leading-relaxed">
              Desktop session recording works without this section. If you also operate connected security cameras, the CCTV+ Plugin
              adds two processing lanes: real-time alerts before a clip is created, and deep forensic intelligence on
              every chunk. All AI runs on-premise with zero data leaving your network.
            </p>
          </div>

          {/* Six capability tiles */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {[
              {
                icon: Activity,
                color: "text-teal-400",
                bg: "bg-teal-500/10",
                border: "border-teal-500/20",
                title: "Hot Path Alerts",
                badge: "Under 2 seconds",
                body: "Real-time analysis of NVR stream frames within 200 milliseconds. Sleeping guard, unauthorized zone entry, and person detection alerts fire before a 5-minute clip is ever created.",
              },
              {
                icon: Database,
                color: "text-emerald-400",
                bg: "bg-emerald-500/10",
                border: "border-emerald-500/20",
                title: "Cold Path Deep Indexing",
                badge: "Every chunk",
                body: "Every 5-minute footage chunk is indexed with OCR text extraction, visual search, NSFW screening, scene analytics (person and vehicle counts), and an AI narrative summary.",
              },
              {
                icon: MessageSquare,
                color: "text-teal-400",
                bg: "bg-teal-500/10",
                border: "border-teal-500/20",
                title: "AI Narrative Summaries",
                badge: "Per segment",
                body: "Each footage segment gets a 2 to 4 sentence AI description: '3 persons detected near the loading dock at 14:32. Vehicle entered gate at 14:35. Guard remained stationary for an extended period.'",
              },
              {
                icon: Search,
                color: "text-emerald-400",
                bg: "bg-emerald-500/10",
                border: "border-emerald-500/20",
                title: "Cross-Camera Search",
                badge: "All cameras",
                body: "Search across all cameras simultaneously by visual concept, OCR text, or AI narrative. Reconstruct a person's route across 8 cameras in one plain-English query to Dex AI.",
              },
              {
                icon: AlertTriangle,
                color: "text-teal-400",
                bg: "bg-teal-500/10",
                border: "border-teal-500/20",
                title: "Immobility Detection",
                badge: "Configurable window",
                body: "Movement tracking across a configurable time window detects security guards who have not moved. Alert fires before the shift ends, not after the incident report arrives.",
              },
              {
                icon: Shield,
                color: "text-emerald-400",
                bg: "bg-emerald-500/10",
                border: "border-emerald-500/20",
                title: "Forensic Export",
                badge: "Chain of custody",
                body: "Package selected footage clips with a tamper-evident manifest and chain-of-custody document. Handover-ready export, stored in your own storage bucket, ready for law enforcement or legal proceedings.",
              },
            ].map((tile) => (
              <div
                key={tile.title}
                className={`rounded-2xl border ${tile.border} p-7 flex flex-col`}
                style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl ${tile.bg} flex items-center justify-center`}>
                    <tile.icon size={20} className={tile.color} />
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${tile.border} ${tile.bg} ${tile.color}`}>
                    {tile.badge}
                  </span>
                </div>
                <h3 className="text-slate-100 font-bold text-base mb-2">{tile.title}</h3>
                <p className="text-on-dark-muted text-sm leading-relaxed flex-1">{tile.body}</p>
              </div>
            ))}
          </div>

          {/* CCTV Profiles subsection */}
          <div
            className="rounded-2xl border border-teal-600/20 p-8"
            style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-teal-500/15 flex items-center justify-center">
                <Server size={20} className="text-teal-400" />
              </div>
              <div>
                <h3 className="text-slate-100 font-bold text-lg">CCTV Profiles</h3>
                <p className="text-on-dark-muted text-sm mt-0.5">
                  Per-camera intelligence configuration. Assign any profile to any number of sources.
                </p>
              </div>
            </div>
            <p className="text-on-dark-muted text-sm leading-relaxed mb-5 max-w-3xl">
              Each camera source is assigned a CCTV profile that controls exactly which AI modules run. Profiles are
              re-read at job dequeue time, so changing a profile affects the next ingested segment without any restart.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                {
                  name: "High-security gate",
                  desc: "All modules: OCR, visual search, NSFW, scene analytics, policy scan, hot path alerts, immobility detection, AI narrative summaries.",
                },
                {
                  name: "Low-CPU warehouse",
                  desc: "Scene analytics only (person and vehicle counts). Minimal CPU impact, suitable for large deployments.",
                },
                {
                  name: "Indoor office",
                  desc: "OCR for OSD text, NSFW screening, and policy scanning. No scene analytics. Hot path disabled.",
                },
              ].map((profile) => (
                <div
                  key={profile.name}
                  className="rounded-xl border border-teal-500/10 p-4"
                  style={{ background: "rgba(13, 148, 136, 0.04)" }}
                >
                  <p className="text-slate-200 text-sm font-semibold mb-1">{profile.name}</p>
                  <p className="text-on-dark-muted text-xs leading-relaxed">{profile.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-teal-500/10 flex flex-wrap items-center justify-between gap-4">
              <p className="text-on-dark-muted text-sm max-w-xl">
                All CCTV AI runs on your own server with CPU inference. No GPU required. No frames
                sent to third-party AI services.
              </p>
              <Link
                to="/pricing#cctv-plugin"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-teal-500/25"
              >
                See CCTV+ Plugin pricing
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-24 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 100%, rgba(13, 148, 136, 0.15) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-teal-600 flex items-center justify-center mx-auto mb-8 shadow-xl shadow-teal-500/30">
            <Shield size={32} className="text-white" />
          </div>
          <h2
            className="text-slate-100 mb-6"
            style={{
              fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              lineHeight: "1.15",
            }}
          >
            Ready to turn{" "}
            <span
              style={{
                background: "linear-gradient(135deg, var(--color-teal-500, #14b8a6) 0%, var(--color-emerald-500, #10b981) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              "we think"
            </span>{" "}
            into{" "}
            <span
              style={{
                background: "linear-gradient(135deg, var(--color-teal-500, #14b8a6) 0%, var(--color-emerald-500, #10b981) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              "we can prove"
            </span>
            ?
          </h2>
          <p className="text-on-dark-muted text-lg mb-10 max-w-2xl mx-auto">
            Schedule a demo to see how DeskReplay creates forensic-grade visual evidence
            for your specific workflows, compliance requirements, and infrastructure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/request-demo"
              className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-10 py-4 rounded-xl text-base font-semibold transition-all hover:shadow-lg hover:shadow-teal-500/25"
            >
              Schedule Demo
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/resources/ciso-guide-non-invasive-forensics"
              className="inline-flex items-center justify-center gap-2 border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 px-10 py-4 rounded-xl text-base font-medium transition-colors"
            >
              Read the CISO Guide
            </Link>
          </div>
          <p className="text-on-dark-muted text-sm mt-8">
            No commitment required. We will tailor the demo to your industry and compliance needs.
          </p>
        </div>
      </section>
      </div>
    </>
  );
}
